import React, { useEffect, useMemo, useState } from 'react'
import { Typography } from 'antd'
import PropTypes from 'prop-types'

import { MultiSelectDropdown } from '~stories'

import { MAX_VENDOR_SECTION_SELECTIONS } from '../../utils/concessionVendorGridDefaults'
import {
  buildVendorLocationFilterKeys,
  getSectionOptionsForSelectedLevels,
  parseVendorLocationFilters
} from '../../utils/concessionVendorLocationFilters'
import styles from '../FAQPageSections/FAQAdminFields.module.css'

const { Text } = Typography

const ConcessionVendorLocationFilter = ({
  hasVenue = false,
  locationError = '',
  locationLevels = [],
  locationPlaceholder = 'All Levels',
  resetKey,
  selectedLocationKeys = [],
  onChange
}) => {
  const parsedSelection = useMemo(
    () => parseVendorLocationFilters(selectedLocationKeys, locationLevels),
    [selectedLocationKeys, locationLevels]
  )

  const parsedLevelKeys = parsedSelection.levelKeys
  const parsedLevelKeySignature = parsedLevelKeys.join('\u0000')

  const [selectedLevelKeys, setSelectedLevelKeys] = useState(parsedLevelKeys)

  useEffect(() => {
    setSelectedLevelKeys(parsedLevelKeys)
  // Sync from stored location keys when the vendor or saved levels change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, parsedLevelKeySignature])

  const levelOptions = useMemo(
    () => locationLevels.map(({ key, label }) => ({ key, label })),
    [locationLevels]
  )

  const sectionOptions = useMemo(
    () => getSectionOptionsForSelectedLevels(locationLevels, selectedLevelKeys),
    [locationLevels, selectedLevelKeys]
  )

  const sectionKeys = parsedSelection.sectionKeys

  const handleLevelChange = nextLevelKeys => {
    const nextLevels = nextLevelKeys || []
    setSelectedLevelKeys(nextLevels)

    const validSectionKeys = sectionKeys.filter(sectionKey =>
      nextLevels.some(levelKey => {
        const level = locationLevels.find(item => item.key === levelKey)
        return (level?.sections || []).some(section => section.key === sectionKey)
      })
    )

    onChange(buildVendorLocationFilterKeys(
      nextLevels,
      validSectionKeys,
      locationLevels
    ))
  }

  const handleSectionChange = nextSectionKeys => {
    onChange(buildVendorLocationFilterKeys(
      selectedLevelKeys,
      nextSectionKeys || [],
      locationLevels
    ))
  }

  return (
    <>
      {!hasVenue && (
        <Text type="secondary">Select a page venue above to load location options.</Text>
      )}
      {hasVenue && !locationLevels.length && (
        <Text type="secondary">No location levels configured for this venue.</Text>
      )}
      <Text
        className={`${styles.fieldLabel}${locationError ? ` ${styles.fieldLabelError}` : ''}`}
        style={{ display: 'block', marginTop: 8 }}
      >
        Level
      </Text>
      <MultiSelectDropdown
        allowClear
        disabled={!hasVenue || !locationLevels.length}
        displayField="label"
        dropdownOptions={hasVenue ? levelOptions : []}
        handleChange={handleLevelChange}
        mode="multiple"
        placeholder={locationPlaceholder}
        selectedItems={selectedLevelKeys}
        showSearch={false}
        valueField="key"
      />
      {sectionOptions.length > 0 && (
        <>
          <Text
            className={`${styles.fieldLabel}${locationError ? ` ${styles.fieldLabelError}` : ''}`}
            style={{ display: 'block', marginTop: 12 }}
          >
            Sections
          </Text>
          <MultiSelectDropdown
            allowClear
            disabled={!selectedLevelKeys.length}
            displayField="label"
            dropdownOptions={sectionOptions}
            handleChange={nextSectionKeys => {
              const selected = nextSectionKeys || []
              if (selected.length > MAX_VENDOR_SECTION_SELECTIONS) return
              handleSectionChange(selected)
            }}
            mode="multiple"
            placeholder="Select sections"
            selectedItems={sectionKeys}
            showSearch={false}
            valueField="key"
          />
          <Text style={{ color: '#8c8c8c', display: 'block', fontSize: 12, marginTop: 4 }}>
            Maximum {MAX_VENDOR_SECTION_SELECTIONS} selections
          </Text>
        </>
      )}
    </>
  )
}

ConcessionVendorLocationFilter.propTypes = {
  hasVenue: PropTypes.bool,
  locationError: PropTypes.string,
  locationLevels: PropTypes.array,
  locationPlaceholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedLocationKeys: PropTypes.array
}

export default ConcessionVendorLocationFilter
